/**
 * A tela de Custos (spec 096, US1).
 *
 * **O NÚMERO É O QUE O SERVIDOR REGISTROU.** A tela não recalcula nada: total,
 * execuções e tokens vêm do mesmo endereço, e a série diária vem do livro-razão
 * — que inclui o turno abandonado. Recontar no cliente produziria um segundo
 * número, e dois números para a mesma pergunta é pior que um número só.
 *
 * Sobre o idioma dos textos, ver o cabeçalho de `executions.tsx`.
 */
import { useCallback, useEffect, useState } from 'react'
import { DollarSign, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EscopoDaOrganizacao } from '@/components/layout/escopo-da-organizacao'
import {
  CartaoDeNumero,
  FaixaDePeriodo,
  SerieEmBarras,
} from '@/components/painel'
import { useOrganizationStore } from '@/stores/organization-store'
import { custosService } from '@/services/painel'
import type { OrganizationCostSummary, Ponto } from '@/schemas/painel'
import { dolar, dolarMiudo, inteiro, milhares } from '@/lib/formato'
import { cn } from '@/lib/utils'

const JANELAS = [
  { valor: 7, rotulo: '7 dias' },
  { valor: 30, rotulo: '30 dias' },
  { valor: 90, rotulo: '90 dias' },
] as const

export function CostsPage() {
  const { currentOrganization } = useOrganizationStore()
  const [dias, setDias] = useState<number>(30)
  const [resumo, setResumo] = useState<OrganizationCostSummary | null>(null)
  const [serie, setSerie] = useState<Ponto[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const buscar = useCallback(async () => {
    if (!currentOrganization) return
    setCarregando(true)
    try {
      const [oResumo, aSerie] = await Promise.all([
        custosService.daOrganizacao(currentOrganization.id, { dias }),
        // A SÉRIE É MELHOR ESFORÇO: ela vem das métricas do atendimento, e quem
        // não tem o módulo não tem série. O total, que é o que a pergunta pede,
        // não depende dela.
        custosService.porDia(currentOrganization.id, { dias }).catch(() => []),
      ])
      setResumo(oResumo)
      setSerie(aSerie)
      setErro(null)
    } catch {
      setErro('Não foi possível ler os custos agora.')
    } finally {
      setCarregando(false)
    }
  }, [currentOrganization, dias])

  useEffect(() => {
    void buscar()
  }, [buscar])

  if (!currentOrganization) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <DollarSign className="mb-4 h-10 w-10 text-amber-400" />
        <p className="text-gray-400">Escolha uma organização para ver os custos.</p>
      </div>
    )
  }

  const porExecucao =
    resumo && resumo.total_executions > 0
      ? resumo.total_cost / resumo.total_executions
      : 0
  const porDia = resumo ? resumo.total_cost / dias : 0
  const tokens = resumo
    ? resumo.total_prompt_tokens + resumo.total_completion_tokens
    : 0

  return (
    <div className="space-y-6">
      {/* O CABEÇALHO DA APLICAÇÃO JÁ DIZ "CUSTOS": repetir como título aqui
          era eco, e custava a primeira dobra da tela. Fica a linha que dá
          contexto — de quem é o dado, que período, e como atualizar. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <FaixaDePeriodo
            opcoes={JANELAS}
            valor={dias}
            aoEscolher={setDias}
            rotulo="Período"
          />
          <p className="text-sm text-gray-500">
            O que o motor registrou — sem recontagem na tela.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EscopoDaOrganizacao nome={currentOrganization.name} />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Atualizar"
            onClick={() => void buscar()}
          >
            <RefreshCw className={cn('h-4 w-4', carregando && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {erro && <p className="text-sm text-red-400">{erro}</p>}

      {resumo && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <CartaoDeNumero
              titulo="Custo no período"
              valor={dolar(resumo.total_cost)}
              detalhe={`média de ${dolar(porDia)} por dia`}
            />
            <CartaoDeNumero
              titulo="Execuções"
              valor={inteiro(resumo.total_executions)}
              detalhe={`nos últimos ${dias} dias`}
            />
            <CartaoDeNumero
              titulo="Custo por execução"
              valor={dolarMiudo(porExecucao)}
              detalhe="total dividido por execuções"
            />
            <CartaoDeNumero
              titulo="Tokens"
              valor={milhares(tokens)}
              detalhe={`${milhares(resumo.total_prompt_tokens)} entrada · ${milhares(
                resumo.total_completion_tokens
              )} saída`}
            />
          </div>

          {serie.length > 0 && (
            <div className="rounded-xl border border-gray-800/50 bg-[#12121a] p-4">
              <p className="mb-3 text-sm font-medium text-gray-300">
                Custo por dia, do livro-razão — inclui o turno abandonado
              </p>
              <SerieEmBarras
                pontos={serie}
                formatar={(v) => dolarMiudo(v)}
                legenda="custo diário, em dólares"
              />
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <Tabela
              titulo="Por agente"
              linhas={resumo.by_agent.map((a) => ({
                id: a.agent_id,
                nome: a.agent_name,
                execucoes: a.total_executions,
                custo: a.total_cost,
              }))}
            />
            <Tabela
              titulo="Por projeto"
              linhas={resumo.by_project.map((p) => ({
                id: p.project_id,
                nome: p.project_name,
                execucoes: p.total_executions,
                custo: p.total_cost,
              }))}
            />
          </div>
        </>
      )}
    </div>
  )
}

interface Linha {
  id: string
  nome: string
  execucoes: number
  custo: number
}

function Tabela({ titulo, linhas }: { titulo: string; linhas: Linha[] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-800/50">
      <p className="bg-white/5 px-4 py-2 text-sm font-medium text-gray-300">
        {titulo}
      </p>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-gray-800/50">
          {linhas.map((linha) => (
            <tr key={linha.id} className="text-gray-300">
              <td className="px-4 py-2">{linha.nome}</td>
              <td className="px-4 py-2 text-right text-gray-500">
                {inteiro(linha.execucoes)}
              </td>
              {/* DUAS CASAS AQUI, e três só no custo por execução: `US$ 17,630`
                  é lido como dezessete mil, e o valor é dezessete e sessenta. */}
              <td className="px-4 py-2 text-right tabular-nums">
                {dolar(linha.custo)}
              </td>
            </tr>
          ))}
          {linhas.length === 0 && (
            <tr>
              <td className="px-4 py-6 text-center text-gray-500" colSpan={3}>
                Nada no período.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
